import styled from "@emotion/styled"
import {
	breakpoint,
	colorContrastLower,
	spacingStaticMd,
	spacingStaticSm,
	spacingStatic2Xs,
} from "@porsche-design-system/components-react/emotion"
import { shouldForwardProp } from "src/common/shouldForwardProp"
import { Skeleton } from "src/components"

interface NotificationListSkeletonProps {
	className?: string
	compact?: boolean
}

export const NotificationListSkeleton = ({ className, compact }: NotificationListSkeletonProps) => {
	return (
		<Wrapper className={className}>
			{Array.from({ length: 3 }, (_, index) => (
				<LayoutMessage $compact={compact} key={index}>
					<ImageHeadingTagsWrapper>
						<Skeleton.Picture $width={compact ? "80px" : "120px"} $aspectRatio="4/3" />

						<HeadingTagsWrapper $compact={compact}>
							<HeadingDateWrapper>
								<Skeleton.Heading size={compact ? "sm" : "md"} />
							</HeadingDateWrapper>
							<TagsContainer>
								{Array.from({ length: 2 }, (_item, i) => (
									<Skeleton.Tag
										key={`${index}-${i}`}
										$width={i === 0 ? "180px" : "80px"}
										compact={compact}
									/>
								))}
							</TagsContainer>
						</HeadingTagsWrapper>
					</ImageHeadingTagsWrapper>

					{!compact && (
						<MessagesWrapper>
							<Skeleton.Text $width="500px" />
							<SkeletonCreationDate $width="80px" size="xs" />
						</MessagesWrapper>
					)}
				</LayoutMessage>
			))}
		</Wrapper>
	)
}

const Wrapper = styled.div`
	display: flex;
	flex-direction: column;
`

const LayoutMessage = styled("div", { shouldForwardProp })<{ $compact?: boolean }>`
	position: relative;
	display: flex;
	flex-direction: column;
	gap: ${spacingStaticSm};
	padding: ${({ $compact }) => ($compact ? spacingStaticSm : spacingStaticMd)};

	${({ $compact }) =>
		!$compact &&
		`
		@media (min-width: ${breakpoint.s}px) {
			display: grid;
			grid-template-columns: minmax(0, 1fr) 200px;
			gap: ${spacingStaticMd};
			justify-items: start;
			align-items: start;
		}
	`}

	&:not(:first-of-type)::before {
		content: "";
		position: absolute;
		top: 0;
		height: 1px;
		width: 100%;
		background: ${colorContrastLower};
	}
`

const ImageHeadingTagsWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${spacingStaticSm};

	@media (min-width: ${breakpoint.s}px) {
		flex-direction: row;
        gap: ${spacingStaticMd};
        align-items: center;
    }
`

const HeadingTagsWrapper = styled("div", { shouldForwardProp })<{ $compact?: boolean }>`
	display: flex;
	flex-direction: column;
	gap: ${({ $compact }) => ($compact ? spacingStatic2Xs : spacingStaticSm)};
`

const HeadingDateWrapper = styled.div`
	display: flex;
	flex-direction: row;
	align-items: baseline;
	gap: ${spacingStaticSm};
`

const MessagesWrapper = styled.div`
	grid-column: 1;
	display: flex;
	flex-direction: column;
`

const SkeletonCreationDate = styled(Skeleton.Text)`
	margin: 1px 0 -1px;
`

const TagsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
	align-items: center;
    gap: ${spacingStaticSm};
`
